import AddOutlined from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteModal from "components/shared/DeleteModal";
import IosSwitch from "components/shared/IosSwitch";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import TitleSeparation from "components/shared/TitleSeparation";
import { formatNumber } from "helpers/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AdvanceProvisionRule, AdvanceProvisionRuleType } from "store/advanceProvision";
import {
    checkGrid,
    checkOverrides,
    formatMonthRange,
    isBlockingGridCheck,
} from "store/advanceProvision/advance-provision-rules";

/** Same limit as the backend payload validation */
const MAX_RULES = 50;

/** Inputs are kept as typed text so that a field can be emptied while editing; `key` identifies the row. */
type DraftRule = Omit<AdvanceProvisionRule, "startMonth" | "endMonth" | "rate"> & {
    key: number;
    startMonth: string;
    endMonth: string;
    rate: string;
};

let nextKey = 0;

const toDraft = (rule: AdvanceProvisionRule): DraftRule => ({
    ...rule,
    key: nextKey++,
    startMonth: String(rule.startMonth),
    endMonth: rule.endMonth === null ? "" : String(rule.endMonth),
    rate: String(rule.rate),
});

const toNumber = (value: string) => (value.trim() === "" ? Number.NaN : Number(value.replace(",", ".")));

const toRule = ({ key: _key, ...draft }: DraftRule): AdvanceProvisionRule => ({
    ...draft,
    label: draft.label?.trim() || null,
    startMonth: toNumber(draft.startMonth),
    endMonth: draft.endMonth.trim() === "" ? null : toNumber(draft.endMonth),
    // Stored as DECIMAL(5,2)
    rate: Math.round(toNumber(draft.rate) * 100) / 100,
});

/**
 * A new priority rule, or a one-month tier inserted before the open-ended tier (which moves one month
 * later) so that the grid stays valid; without an open-ended tier, the new one starts after the last end.
 */
const withNewRow = (ruleType: AdvanceProvisionRuleType, drafts: DraftRule[]): DraftRule[] => {
    if (ruleType === "override") {
        return [
            ...drafts,
            toDraft({
                label: "",
                conditionType: "term_ended",
                startMonth: 0,
                endMonth: null,
                rate: 100,
                isActive: true,
            }),
        ];
    }
    const openEnded = drafts.find((draft) => draft.isActive && draft.endMonth.trim() === "");
    const openStart = openEnded ? toNumber(openEnded.startMonth) : Number.NaN;
    if (openEnded && Number.isInteger(openStart)) {
        const inserted = toDraft({
            label: null,
            conditionType: null,
            startMonth: openStart,
            endMonth: openStart,
            rate: 0,
            isActive: true,
        });
        return drafts.flatMap((draft) =>
            draft === openEnded
                ? [
                      { ...inserted, rate: "" },
                      { ...draft, startMonth: String(openStart + 1) },
                  ]
                : [draft],
        );
    }
    const lastEnd = Math.max(-1, ...drafts.map((draft) => toNumber(draft.endMonth)).filter(Number.isFinite));
    return [
        ...drafts,
        {
            ...toDraft({
                label: null,
                conditionType: null,
                startMonth: lastEnd + 1,
                endMonth: null,
                rate: 0,
                isActive: true,
            }),
            rate: "",
        },
    ];
};

const cellInput =
    "border border-border-color rounded h-8 px-2 py-1 text-sm w-full outline-none focus:border-info disabled:bg-backgroundZ1 disabled:cursor-not-allowed";
const headerCell = "text-left font-normal text-foreground px-4 py-2 bg-backgroundZ2";
const bodyCell = "px-4 py-2 border-t border-border-color";

type Props = {
    ruleType: AdvanceProvisionRuleType;
    rules: AdvanceProvisionRule[];
    defaults: AdvanceProvisionRule[];
    canEdit: boolean;
    onSave: (rules: AdvanceProvisionRule[]) => Promise<void>;
};

const RuleBlock = ({ ruleType, rules, defaults, canEdit, onSave }: Props) => {
    const [t] = useTranslation();
    const isOverride = ruleType === "override";
    const [drafts, setDrafts] = useState<DraftRule[]>(() => rules.map(toDraft));
    const [isSaving, setIsSaving] = useState(false);
    const [isRestoreOpen, setIsRestoreOpen] = useState(false);

    useEffect(() => setDrafts(rules.map(toDraft)), [rules]);

    const parsed = useMemo(() => drafts.map(toRule), [drafts]);
    const gridCheck = useMemo(() => (isOverride ? null : checkGrid(parsed)), [isOverride, parsed]);
    const isBlocked = gridCheck ? isBlockingGridCheck(gridCheck) : checkOverrides(parsed) === "invalid";
    const isDirty = JSON.stringify(parsed) !== JSON.stringify(rules);

    const update = (index: number, patch: Partial<DraftRule>) =>
        setDrafts((current) => current.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)));

    const save = async (next: AdvanceProvisionRule[]) => {
        setIsSaving(true);
        // Grids are stored by ascending start month, priority rules keep their order
        const ordered = isOverride ? next : [...next].sort((a, b) => a.startMonth - b.startMonth);
        await onSave(ordered);
        setIsSaving(false);
    };

    const banner = (() => {
        if (!gridCheck) {
            return isBlocked ? (
                <MessageBlock type="error" text={t("AdvanceProvision.Settings.InvalidOverride")} />
            ) : null;
        }
        switch (gridCheck.kind) {
            case "invalid":
                return <MessageBlock type="error" text={t("AdvanceProvision.Settings.InvalidValues")} />;
            case "overlap":
                return (
                    <MessageBlock
                        type="error"
                        text={t("AdvanceProvision.Settings.Overlap", {
                            first: formatMonthRange(gridCheck.first),
                            second: formatMonthRange(gridCheck.second),
                        })}
                    />
                );
            case "missingInfinite":
                return <MessageBlock type="error" text={t("AdvanceProvision.Settings.MissingInfinite")} />;
            case "gap":
                return (
                    <MessageBlock
                        type="warning"
                        text={t("AdvanceProvision.Settings.Gap", { from: gridCheck.from, to: gridCheck.to })}
                    />
                );
            case "complete":
                return (
                    <p className="m-0 rounded bg-success-light px-4 py-2 text-xs text-success">
                        {t("AdvanceProvision.Settings.Complete")}
                    </p>
                );
        }
    })();

    const activeSlices = parsed
        .filter((rule) => rule.isActive && Number.isFinite(rule.startMonth) && Number.isFinite(rule.rate))
        .sort((a, b) => a.startMonth - b.startMonth);

    return (
        <section className="mt-10 flex flex-col gap-3">
            <TitleSeparation
                title={t(`AdvanceProvision.Settings.${ruleType}.Title`)}
                description={t(`AdvanceProvision.Settings.${ruleType}.Description`)}
            />
            <div className="overflow-x-auto rounded border border-border-color bg-white">
                <table className="w-full border-collapse text-xs">
                    <thead>
                        <tr>
                            {isOverride ? (
                                <>
                                    <th className={headerCell}>{t("AdvanceProvision.Settings.Label")}</th>
                                    <th className={headerCell}>{t("AdvanceProvision.Settings.Condition")}</th>
                                </>
                            ) : (
                                <>
                                    <th className={headerCell}>{t("AdvanceProvision.Settings.StartMonth")}</th>
                                    <th className={headerCell}>{t("AdvanceProvision.Settings.EndMonth")}</th>
                                </>
                            )}
                            <th className={headerCell}>{t("AdvanceProvision.Settings.Rate")}</th>
                            <th className={`${headerCell} text-center`}>{t("AdvanceProvision.Settings.Active")}</th>
                            {canEdit && (
                                <th className={headerCell} aria-label={t("AdvanceProvision.Settings.Delete")} />
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {drafts.map((draft, index) => (
                            <tr key={draft.key}>
                                {isOverride ? (
                                    <>
                                        <td className={bodyCell}>
                                            <input
                                                className={cellInput}
                                                value={draft.label ?? ""}
                                                maxLength={255}
                                                disabled={!canEdit}
                                                aria-label={t("AdvanceProvision.Settings.Label")}
                                                onChange={(event) => update(index, { label: event.target.value })}
                                            />
                                        </td>
                                        <td className={`${bodyCell} text-sm`}>
                                            {t("AdvanceProvision.Settings.ConditionTermEnded")}
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td className={`${bodyCell} w-32`}>
                                            <input
                                                className={`${cellInput} text-right`}
                                                inputMode="numeric"
                                                value={draft.startMonth}
                                                disabled={!canEdit}
                                                aria-label={t("AdvanceProvision.Settings.StartMonth")}
                                                onChange={(event) => update(index, { startMonth: event.target.value })}
                                            />
                                        </td>
                                        <td className={`${bodyCell} w-32`}>
                                            <input
                                                className={`${cellInput} text-right`}
                                                inputMode="numeric"
                                                placeholder="∞"
                                                value={draft.endMonth}
                                                disabled={!canEdit}
                                                aria-label={t("AdvanceProvision.Settings.EndMonth")}
                                                onChange={(event) => update(index, { endMonth: event.target.value })}
                                            />
                                        </td>
                                    </>
                                )}
                                <td className={`${bodyCell} w-32`}>
                                    <div className="flex items-center gap-1.5">
                                        <input
                                            className={`${cellInput} text-right`}
                                            inputMode="decimal"
                                            value={draft.rate}
                                            disabled={!canEdit}
                                            aria-label={t("AdvanceProvision.Settings.Rate")}
                                            onChange={(event) => update(index, { rate: event.target.value })}
                                        />
                                        <span className="text-foreground">%</span>
                                    </div>
                                </td>
                                <td className={`${bodyCell} w-20 text-center`}>
                                    <IosSwitch
                                        checked={draft.isActive}
                                        disabled={!canEdit}
                                        inputProps={{ "aria-label": t("AdvanceProvision.Settings.Active") }}
                                        onChange={(event) => update(index, { isActive: event.target.checked })}
                                    />
                                </td>
                                {canEdit && (
                                    <td className={`${bodyCell} w-12 text-right`}>
                                        <button
                                            type="button"
                                            className="cursor-pointer border-0 bg-transparent text-foreground hover:text-danger-text"
                                            aria-label={t("AdvanceProvision.Settings.Delete")}
                                            onClick={() =>
                                                setDrafts((current) => current.filter((_, i) => i !== index))
                                            }
                                        >
                                            <DeleteOutlineOutlined fontSize="small" />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {banner && <div className="px-4 py-3">{banner}</div>}
            </div>
            {!isOverride && activeSlices.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-xs text-foreground">
                    <span>{t("AdvanceProvision.Settings.ActiveSlices")}</span>
                    {activeSlices.map((rule) => (
                        <span
                            key={`${rule.startMonth}-${rule.endMonth}`}
                            className="rounded border border-border-color bg-backgroundZ1 px-2 py-0.5"
                        >
                            {t("AdvanceProvision.Settings.SliceSummary", {
                                range: formatMonthRange(rule),
                                rate: formatNumber(rule.rate),
                            })}
                        </span>
                    ))}
                </div>
            )}
            {canEdit && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex gap-2">
                        <StyledButton
                            buttonType="secondary"
                            startIcon={<AddOutlined fontSize="small" />}
                            disabled={drafts.length >= MAX_RULES}
                            onClick={() => setDrafts((current) => withNewRow(ruleType, current))}
                        >
                            {t(
                                isOverride
                                    ? "AdvanceProvision.Settings.AddOverride"
                                    : "AdvanceProvision.Settings.AddSlice",
                            )}
                        </StyledButton>
                        <StyledButton buttonType="secondary" onClick={() => setIsRestoreOpen(true)}>
                            {t("AdvanceProvision.Settings.RestoreDefaults")}
                        </StyledButton>
                    </div>
                    <StyledButton
                        buttonType="primary"
                        loading={isSaving}
                        disabled={isBlocked || !isDirty || isSaving}
                        onClick={() => save(parsed)}
                    >
                        {t("Common.Save")}
                    </StyledButton>
                </div>
            )}
            <DeleteModal
                open={isRestoreOpen}
                onClose={() => setIsRestoreOpen(false)}
                onDelete={async () => {
                    await save(defaults);
                    setIsRestoreOpen(false);
                }}
                elementTitle={t(`AdvanceProvision.Settings.${ruleType}.Title`)}
                title={t("AdvanceProvision.Settings.RestoreDefaults")}
                description={t("AdvanceProvision.Settings.RestoreDescription", {
                    element: t(`AdvanceProvision.Settings.${ruleType}.Title`),
                })}
                warningMessage={t("AdvanceProvision.Settings.RestoreWarning")}
                confirmLabel={t("AdvanceProvision.Settings.RestoreConfirm")}
                confirmButtonType="primary"
            />
        </section>
    );
};

export default RuleBlock;
